import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import EsgCard from "../Esg/EsgCard";
import StartedWith from "../Esg/StartedWith";
import { useModal } from "../../contexts/ModalContext";

function FAQAccordion() {
    const [openIndex, setOpenIndex] = useState(0); // Default to first item open

    const faqs = [
        {
            question: "What is CBAM, and why is reporting required?",
            answer: "The Carbon Border Adjustment Mechanism (CBAM) is an EU regulation that places a carbon cost on certain imported goods to align them with EU climate standards. EU importers must report the embedded carbon emissions of CBAM-covered goods in the official EU CBAM Registry. Exporters play a critical role by providing accurate, product-level emissions data to their EU customers, helping avoid default values, penalties, and loss of competitiveness."
        },
        {
            question: "What emissions need to be reported under CBAM?",
            answer: "CBAM requires reporting of embedded carbon emissions, which includes direct emissions from production processes and indirect emissions from electricity consumption. The reporting must follow CBAM methodology and cover all emissions associated with the production of covered goods."
        },
        {
            question: "How do I know if my product is covered under CBAM?",
            answer: "CBAM currently covers six key sectors: steel, cement, aluminium, fertilisers, hydrogen, and electricity. If your product falls into any of these categories and is exported to the EU, it is likely covered under CBAM. The regulation may expand to include additional sectors in the future."
        },
        {
            question: "What are precursors in CBAM reporting?",
            answer: "Precursors are intermediate materials or components used in the production of CBAM-covered goods. For example, in steel production, precursors might include iron ore, coal, or other raw materials. CBAM requires reporting of emissions from precursors, which means exporters need to collect and validate supplier data for purchased precursors."
        },
        {
            question: "Who is responsible for CBAM reporting and compliance?",
            answer: "EU importers are legally responsible for CBAM reporting and compliance. However, exporters play a critical role by providing accurate, product-level emissions data to their EU customers. Exporters who provide accurate data help importers avoid default values and penalties, maintaining competitiveness in the EU market."
        },
        {
            question: "What are the CBAM reporting timelines?",
            answer: "CBAM reporting is ongoing and requires quarterly submissions. The reporting cycles are frequent and importer-driven, meaning exporters need to provide updated emissions data regularly to support their EU customers' compliance obligations. CBAM is not a one-time disclosure but requires ongoing, repeatable reporting backed by defensible data."
        }
    ];

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <div className="space-y-4">
            {faqs.map((faq, index) => (
                <div key={index} className="pb-4 border-b border-gray-300">
                    <button
                        onClick={() => toggleAccordion(index)}
                        className="flex items-center justify-between w-full text-left"
                    >
                        <h3 className="text-base font-semibold text-gray-800 md:text-lg montserrat">
                            {faq.question}
                        </h3>
                        <span className="flex-shrink-0 ml-4">
                            <div className="flex items-center justify-center w-6 h-6 bg-orange-500 rounded-full">
                                {openIndex === index ? (
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 10L2 6L6 2" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                ) : (
                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M6 2L2 6L6 10" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </div>
                        </span>
                    </button>
                    {openIndex === index && (
                        <p className="mt-3 text-sm leading-relaxed text-gray-600 md:text-base montserrat">
                            {faq.answer}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

function WhoItsBuiltForAccordion() {
    const [openIndex, setOpenIndex] = useState(0); // Default to first item open

    const audiences = [
        {
            title: "Exporters",
            description: "Streamline CBAM compliance and reporting for your exports to the EU. cbamSuite enables accurate product-level emissions calculation and automated CBAM declaration generation, ensuring you stay competitive in the EU market."
        },
        {
            title: "Manufacturers",
            description: "Simplify CBAM reporting for your products exported to the EU. cbamSuite helps manufacturers calculate accurate carbon emissions, generate EU-ready declarations, and maintain compliance without complex spreadsheets or lengthy processes."
        },
        {
            title: "Sustainability Teams",
            description: "Transform CBAM compliance into strategic advantage. cbamSuite helps internal teams manage product-level carbon emissions, generate accurate CBAM declarations, and turn compliance into competitive benefits with structured automation."
        },
        {
            title: "Sustainability Consultants",
            description: "Deliver faster, more accurate CBAM reporting services for your clients. cbamSuite empowers consultants to provide comprehensive CBAM compliance services with audit-ready outputs, enhancing your service delivery and client satisfaction."
        }
    ];

    const toggleAccordion = (index) => {
        setOpenIndex(openIndex === index ? -1 : index);
    };

    return (
        <div className="space-y-4">
            {audiences.map((audience, index) => (
                <div key={index} className="pb-4 border-b border-gray-400">
                    <button
                        onClick={() => toggleAccordion(index)}
                        className="flex items-center justify-between w-full text-left"
                    >
                        <h3 className="text-lg font-medium text-white md:text-xl montserrat">
                            {audience.title}
                        </h3>
                        <span className="flex-shrink-0 ml-4 text-2xl text-white">
                            {openIndex === index ? (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M19 9L12 16L5 9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            ) : (
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M9 5L16 12L9 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            )}
                        </span>
                    </button>
                    {openIndex === index && audience.description && (
                        <p className="mt-3 text-sm text-gray-200 md:text-base montserrat">
                            {audience.description}
                        </p>
                    )}
                </div>
            ))}
        </div>
    );
}

function Cbam() {
    const navigate = useNavigate();
    const { openModal } = useModal();

    const handleBookDemo = () => {
        openModal();
    };

    const handleGetStarted = () => {
        window.location.href = 'https://cbamsuite.com/register-page';
    };

    const handleContactUs = () => {
        navigate('/contact-us');
    };

    const handleExplorePlans = () => {
        window.location.href = 'https://cbamsuite.com/#plans';
    };

    const howWeHelp = [
        {
            "title": "TRANSLATES REGULATION INTO CLEAR WORKFLOWS",
            "image": "/simplifieddata.png",
            "description": "CBAM requirements are built directly into the platform, guiding users through data capture, calculation, and reporting without ambiguity."
        },
        {
            "title": "CENTRALISES CBAM DATA",
            "image": "/valuechain.png",
            "description": "Production inputs, energy consumption, and precursor information are managed in one place, ensuring consistency across products and reporting periods."
        },
        {
            "title": "PRODUCES EU-READY CBAM DECLARATIONS",
            "image": "/automated.png",
            "description": "Has the ability to generate customer-wise CBAM Communication sheets that importers from the EU can use directly for registry submissions."
        },
        {
            "title": "AUTOMATES EMISSION CALCULATIONS",
            "image": "/seemlesserp.png",
            "description": "Embedded emissions are calculated using standardised logic aligned with CBAM rules-reducing manual effort and calculation risk."
        },
        {
            "title": "REDUCES DEPENDENCE ON DEFAULT VALUES",
            "image": "/realtimedashboards.png",
            "description": "Accurate, verified data helps exporters avoid default emission factors that can significantly increase CBAM exposure."
        },
        {
            "title": "SUPPLIER DATA INTEGRATION",
            "image": "/unlimited users.png",
            "description": "Import emissions data for purchased precursors to improve completeness and reporting accuracy."
        }
    ];

    const whyMyKarbFeatures = [
        {
            title: "SIMPLIFIES COMPLEX REGULATIONS",
            description: "Automatically interprets evolving compliance requirements into clear, easy-to-follow actions.",
            image: "/simplifieddata.png",
            alt: "Simplifies Complex Regulations"
        },
        {
            title: "CENTRALIZES SUPPLY CHAIN DATA",
            description: "Brings all your sustainability data into one reliable, integrated platform.",
            image: "/valuechain.png",
            alt: "Centralizes Supply Chain Data"
        },
        {
            title: "AUTOMATES REPORTING",
            description: "Generates accurate reports in minutes – saving time and effort.",
            image: "/automated.png",
            alt: "Automates Reporting"
        },
        {
            title: "ELIMINATES MANUAL ERRORS",
            description: "Replaces spreadsheets with automated, validated data flows.",
            image: "/seemlesserp.png",
            alt: "Eliminates Manual Errors"
        },
        {
            title: "ENHANCES INTERNAL EXPERTISE",
            description: "Guides teams with built-in intelligence and regulatory insights.",
            image: "/realtimedashboards.png",
            alt: "Enhances Internal Expertise"
        },
        {
            title: "TRANSFORMS COMPLIANCE INTO STRATEGY",
            description: "Turns data into decisions that drive measurable sustainability impact.",
            image: "/unlimited users.png",
            alt: "Transforms Compliance Into Strategy"
        }
    ];

    const aiPoweredFeatures = [
        {
            title: "Automation at Scale",
            description: "LCA – PCF calculations and CBAM reporting reduced from weeks to minutes, eliminating manual errors and inefficiencies.",
            icon: (
                <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            )
        },
        {
            title: "Intelligence for Clarity",
            description: "Validate data, identify environmental hotspots, and interpret complex regulations with AI-powered precision, ensuring audit-ready reports.",
            icon: (
                <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
            )
        },
        {
            title: "Predictive Insights",
            description: "Leverage ML models to anticipate compliance risks, forecast environmental impacts, and develop proactive sustainability strategies.",
            icon: (
                <svg className="w-12 h-12 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
            )
        }
    ];

    const modules = [
        // {
        //     title: "Life Cycle Assessment (LCA) – Product Carbon Footprint (PCF) Module",
        //     features: [
        //         "Measure the environmental footprint of products in minutes.",
        //         "Identify hotspots across raw materials, production, and operations.",
        //         "Support cost efficiency and strategic sustainability decisions.",
        //         "Fast-track environmental assessments with clear, accurate dashboards."
        //     ],
        //     image: "/mykarb3.png",
        //     imagePosition: "left" // left or right
        // },
        {
            title: "Carbon Border Adjustment Mechanism (CBAM) Module",
            features: [
                "Seamlessly comply with EU trade regulations without needing experts.",
                "Intuitive interface to import data directly from suppliers.",
                "Download complete CBAM declarations ready for submission.",
                "Reduce reporting errors, save time, and stay aligned with evolving policies."
            ],
            image: "/cbam-dashboard.png",
            imagePosition: "left"
        }
    ];

    return (
        <div className="flex flex-col justify-center text-white bg-white">
            <Helmet>
                <title>CBAM Compliance Software for Manufacturers | Nirantara Solutions</title>
                <meta name="description" content="Nirantara's CBAM compliance software helps manufacturers and exporters automate carbon border adjustment mechanism reporting, meet EU CSRD and CBAM requirements, and simplify supply chain emissions tracking with cbamSuite." />
                <meta name="keywords" content="CBAM Compliance Software for Manufacturers, CBAM advisory services for exporters, ESG consulting for export-oriented manufacturers, CBAM compliance consulting, cbamSuite platform, CSRD and CBAM compliance services, sustainability consulting for manufacturing industry" />
                <link rel="canonical" href="https://nirantara.solutions/cbam" />
            </Helmet>
            <div className="flex flex-col justify-center text-white bg-white">
                <div className="flex flex-col justify-center w-full bg-white max-md:max-w-full">
                    <section className="flex overflow-hidden relative flex-col justify-end items-start px-8 md:px-16 lg:px-24 pb-16 md:pb-20 pt-32 w-full min-h-[calc(100vh)] max-md:px-5 max-md:pb-12 max-md:max-w-full">
                        <img
                            loading="lazy"
                            src="/cbam1.jpg"
                            alt="cbamSuite Hero Background"
                            className="absolute inset-0 object-cover size-full"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50"></div>
                        <div className="flex z-10 relative flex-col items-start w-full max-w-[600px] max-md:max-w-full">
                            <h2 className="text-4xl font-black leading-tight text-left text-white md:text-5xl lg:text-6xl max-md:text-3xl helvetica">
                                cbamSuite
                            </h2>
                            <button
                                onClick={handleGetStarted}
                                className="flex items-center gap-3 px-8 py-4 mt-6 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                style={{ backgroundColor: '#2C5F7C' }}
                            >
                                <span className="montserrat">Get Started</span>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </section>

                    {/* EU Trade Carbon Compliance Section */}
                    <section className="w-full py-16 bg-gray-50 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h1 className="mb-8 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                EU Trade Carbon Compliance Made Easier
                            </h1>
                            <div className="space-y-6 text-gray-700">
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                    The Carbon Border Adjustment Mechanism (CBAM) is a European Union regulation that applies a carbon cost to certain imported goods, aligning them with the environmental standards followed by EU manufacturers. It covers key sectors including steel, cement, aluminium, fertilisers, hydrogen, and electricity.
                                </p>
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                    For exporters, CBAM requires accurate reporting of product-level carbon emissions in line with EU guidelines. This information is used by EU importers to meet regulatory obligations and avoid default emission values. CBAM promotes transparency in international trade while encouraging exporters to strengthen emissions management and remain competitive in the EU market.
                                </p>
                            </div>
                            <div className="mt-12">
                                <h3 className="mb-6 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px' }}>
                                    What is cbamSuite?
                                </h3>
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px', color: '#374151' }}>
                                    cbamSuite is a purpose-built CBAM reporting platform designed specifically for exporters covered under the EU CBAM regulation. It supports businesses trading in steel, cement, aluminium, fertilisers, hydrogen, and electricity by enabling accurate product-level emissions calculation and the generation of EU-ready CBAM declarations.
                                </p>
                                <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px', color: '#374151', marginTop: '24px' }}>
                                    Built for manufacturers, exporters, and sustainability teams, cbamSuite ensures that emission data shared with EU importers is consistent, complete, and aligned with CBAM methodology, reducing reliance on default values and last-minute rework.
                                </p>
                            </div>
                        </div>
                    </section>

                    {/* Why Exporters to the EU Need Help with CBAM Documentation Section */}
                    <section className="w-full py-16 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%', backgroundColor: '#2C5F7C' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-12 text-center text-white md:mb-16" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Why Exporters to the EU Need Help with CBAM Documentation
                            </h2>

                            <div className="grid items-center grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12">
                                <div className="text-white">
                                    <p className="mb-8" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        Many exporters struggle with CBAM compliance because of several process and documentation challenges:
                                    </p>
                                    <div className="space-y-6">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                                Emissions data is spread across production, utilities, and procurement teams
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                                Product-level calculations are complex and time-consuming
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                                Supplier data for purchased precursors is difficult to collect and validate
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                                Spreadsheets lack traceability and audit readiness dashboards
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                                Reporting cycles are frequent and importer-driven
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            </div>
                                            <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                                Small errors can lead to default values and higher carbon costs
                                            </p>
                                        </div>
                                    </div>
                                    <p className="mt-8" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        CBAM is not a one-time disclosure. It requires ongoing, repeatable reporting backed by defensible data.
                                    </p>
                                </div>
                                <div className="w-full h-full">
                                    <img
                                        src="/cbam2.png"
                                        alt="CBAM compliance challenge"
                                        className="object-cover w-full h-full rounded-lg shadow-2xl"
                                        style={{ border: '2px solid #87CEEB' }}
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* How CbamSuite Supports Exporters To Comply With CBAM Section */}
                    <div className="flex flex-col items-center w-full gap-10 sm:gap-14 px-[2rem] py-[2rem] sm:px-[5rem] md:px-[5rem] lg:px-[15rem] montserrat" style={{ backgroundColor: '#ECECEC' }}>
                        <h2 className="font-bold text-center text-black sm:pt-3" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                            How CbamSuite Supports Exporters To Comply With CBAM
                        </h2>
                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 md:gap-6 w-full">
                            {howWeHelp && howWeHelp.length > 0 && howWeHelp.map((item, index) => {
                                const { title, subtitle, description, cta, image } = item;
                                return (
                                    <div key={index} className="h-full">
                                        <EsgCard minHeight="100%" title={title} subtitle={subtitle} description={description} cta={cta} Icon={image} className="h-full" />
                                    </div>
                                )
                            })}
                        </div>
                        <div className="mt-8">
                            <button
                                onClick={handleGetStarted}
                                className="flex items-center gap-2 px-8 py-4 text-base font-semibold text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                style={{ backgroundColor: '#1C486B' }}
                            >
                                <span className="montserrat">Start Your ESG Journey</span>
                                <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* About MyKarb Section */}
                    {/* <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2 lg:gap-12 items-center">
                                <div>
                                    <h2 className="mb-6 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                        About MyKarb
                                    </h2>
                                    
                                    <div className="space-y-4 mb-6 text-gray-700">
                                        <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                            At Nirantara, we built myKarb to simplify sustainability compliance for businesses of all sizes. Powered by automation and AI, myKarb helps organizations measure, manage, and report their environmental footprint with speed and precision.
                                        </p>
                                        <p style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                            From Life Cycle Assessments (LCA)- Product Carbon Footprint (PCF) to Carbon Border Adjustment Mechanism (CBAM) compliance, myKarb turns complex regulations into simple, actionable insights, helping businesses save time, reduce risk, and make smarter sustainability decisions.
                                        </p>
                                    </div>

                                    <div className="space-y-4 mb-6">
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <p className="text-base font-medium text-gray-800 montserrat md:text-lg">
                                                Faster LCA - PCF Processing
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                                </svg>
                                            </div>
                                            <p className="text-base font-medium text-gray-800 montserrat md:text-lg">
                                                Seamless CBAM Reporting
                                            </p>
                                        </div>
                                        <div className="flex items-start gap-3">
                                            <div className="flex-shrink-0 mt-1">
                                                <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                                                </svg>
                                            </div>
                                            <p className="text-base font-medium text-gray-800 montserrat md:text-lg">
                                                AI-Powered Insights
                                            </p>
                                        </div>
                                    </div>

                                    <p className="mb-8 text-gray-700" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        Whether you're a manufacturer, importer, or consultant, myKarb delivers clarity, speed, and confidence across your sustainability journey.
                                    </p>

                                    <div className="flex flex-col gap-4 sm:flex-row">
                                        <button
                                            onClick={handleContactUs}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-blue-600 transition-all duration-300 border-2 border-blue-600 rounded-lg md:text-lg hover:bg-blue-50 montserrat"
                                        >
                                            Contact Us
                                        </button>
                                        <button
                                            onClick={handleGetStarted}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90 montserrat"
                                            style={{ backgroundColor: '#2C5F7C' }}
                                        >
                                            Get Started
                                        </button>
                                    </div>
                                </div>

                                <div className="w-full h-full">
                                    <img
                                        src="/mykarb2new.jpg"
                                        alt="MyKarb Sustainability Platform"
                                        className="object-cover w-full h-full rounded-lg shadow-2xl"
                                    />
                                </div>
                            </div>
                        </div>
                    </section> */}

                    {/* Features of myKarb Section */}
                    {/* <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-4 text-center text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                            Why myKarb? 
                            </h2>

                            <div className="grid grid-cols-1 gap-6 mb-12 md:grid-cols-2 lg:grid-cols-3 md:gap-8">
                                {whyMyKarbFeatures.map((feature, index) => (
                                    <div key={index} className="flex flex-col items-center p-6 text-center rounded-lg md:p-8">
                                        <div className="flex items-center justify-center w-24 h-24 p-4 mb-6 overflow-hidden rounded-2xl">
                                            <img
                                                src={feature.image}
                                                alt={feature.alt}
                                                className="object-contain w-full h-full"
                                            />
                                        </div>
                                        <h3 className="mb-3 text-xl font-semibold text-gray-800 montserrat">{feature.title}</h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                            {feature.description}
                                        </p>
                                    </div>
                                ))}
                            </div>

                            <div className="flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
                                <button
                                    onClick={handleContactUs}
                                    className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-blue-600 transition-all duration-300 border-2 border-blue-600 rounded-lg md:text-lg hover:bg-blue-50 montserrat"
                                >
                                    Contact Us
                                </button>
                                <button
                                    onClick={handleGetStarted}
                                    className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90 montserrat"
                                    style={{ backgroundColor: '#2C5F7C' }}
                                >
                                    Get Started
                                </button>
                            </div>
                        </div>
                    </section> */}

                    {/* Smarter Compliance Powered by AI Section */}
                    {/* <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-16 text-center text-black md:mb-20" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Smarter Compliance Powered by AI
                            </h2>

                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3 md:gap-10">
                                {aiPoweredFeatures.map((feature, index) => (
                                    <div 
                                        key={index} 
                                        className={`flex flex-col items-center p-6 text-center ${
                                            index === 0 ? 'border-b border-r border-gray-200' : 
                                            index === 1 ? 'border-b border-r border-gray-200' : 
                                            'border-b border-gray-200'
                                        }`}
                                    >
                                        <div className="mb-6">
                                            {feature.icon}
                                        </div>
                                        <h3 className="mb-3 text-xl font-semibold text-black montserrat">{feature.title}</h3>
                                        <p className="text-sm leading-relaxed text-gray-600 montserrat">
                                            {feature.description}
                                        </p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section> */}

                    {/* Our Modules Section */}
                    <section className="w-full py-16 bg-gray-50 md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-12 text-center text-black md:mb-16" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Our Modules
                            </h2>

                            {modules.map((module, index) => (
                                <div
                                    key={index}
                                    className={`grid items-center grid-cols-1 gap-8 mb-16 lg:grid-cols-2 lg:gap-12 md:mb-24 ${index === modules.length - 1 ? 'mb-12' : ''
                                        }`}
                                >
                                    {/* Image */}
                                    <div className={`relative ${module.imagePosition === 'left' ? 'order-2 lg:order-1' : 'order-2 lg:order-2'}`}>
                                        <div className="absolute bottom-0 w-3/4 transform -translate-x-1/2 rounded-full left-1/2 h-3/4" style={{ backgroundColor: '#EB7E0E66', backdropFilter: 'blur(100px)', filter: 'blur(100px)', zIndex: 0 }}></div>
                                        <img
                                            src={module.image}
                                            alt={module.title}
                                            className="relative z-10 w-full rounded-lg shadow-2xl"
                                        />
                                    </div>

                                    {/* Content */}
                                    <div className={module.imagePosition === 'left' ? 'order-1 lg:order-2' : 'order-1 lg:order-1'}>
                                        <h3 className="mb-6 text-2xl font-semibold text-black md:text-3xl lg:text-4xl md:mb-8" style={{ fontFamily: 'Poppins', fontWeight: 600 }}>
                                            {module.title}
                                        </h3>
                                        <ul className="space-y-3 text-base leading-relaxed text-gray-600 md:text-lg montserrat">
                                            {module.features.map((feature, featureIndex) => (
                                                <li key={featureIndex} className="flex items-start">
                                                    <span className="flex-shrink-0 w-2 h-2 mt-2 mr-3 rounded-full bg-orange-500"></span>
                                                    <span>{feature}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            ))}

                            {/* Get Started Button */}
                            {/* <div className="flex justify-center mt-12 md:mt-16">
                                <button
                                    onClick={handleGetStarted}
                                    className="flex items-center gap-3 px-8 py-4 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90"
                                    style={{ backgroundColor: '#2C5F7C' }}
                                >
                                    <span className="montserrat">Get Started</span>
                                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M4 10H16M16 10L10 4M16 10L10 16" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </button>
                            </div> */}
                        </div>
                    </section>

                    {/* StartedWith Section */}
                    <StartedWith
                        heading={"The Right CBAM Plan for Growing Exporters."}
                        content1={"The Right CBAM Plan for Growing Exporters."}
                        content2={"Drive your carbon journey with confidence, using Nirantara's intelligent compliance platform, built to grow with your business."}
                        button1={"Explore Plans"}
                        button2={"Get Started Today"}
                        btn1OnClick={handleExplorePlans}
                        btn2OnClick={handleGetStarted}
                    />

                    {/* Frequently Asked Questions Section */}
                    <section className="w-full py-16 bg-white md:py-24" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <h2 className="mb-12 text-center text-black md:mb-16" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                Frequently Asked Questions
                            </h2>
                            <FAQAccordion />
                        </div>
                    </section>

                    {/* Your CBAM Reporting Process Scalable Across Reporting Cycles Section */}
                    <section className="w-full py-8 bg-white md:py-12" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
                                <div>
                                    <h2 className="mb-6 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                        Your CBAM Reporting Process Scalable Across Reporting Cycles?
                                    </h2>
                                    <p className="mb-8 text-gray-600" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        cbamSuite supports repeatable, scalable CBAM reporting through structured data management and consistent calculation methods.
                                    </p>
                                    <div className="flex flex-col gap-4 sm:flex-row">
                                        <button
                                            onClick={handleContactUs}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium transition-all duration-300 border-2 rounded-lg md:text-lg hover:bg-blue-50 montserrat"
                                            style={{ color: '#1C486B', borderColor: '#1C486B', backgroundColor: 'white' }}
                                        >
                                            Contact Us
                                        </button>
                                        <button
                                            onClick={handleGetStarted}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90 montserrat"
                                            style={{ backgroundColor: '#1C486B' }}
                                        >
                                            Get Started Today
                                        </button>
                                    </div>
                                </div>
                                <div className="flex justify-center lg:justify-end">
                                    <img
                                        src="/vajra-6.png"
                                        alt="LCA and PCF Reporting Platform"
                                        className="object-contain w-full h-auto max-w-md"
                                    />
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* Struggling to Measure and Manage ESG Performance at Scale Section */}
                    <section className="w-full py-8 bg-white md:py-12" style={{ paddingLeft: '8%', paddingRight: '8%' }}>
                        <div className="max-w-[1400px] mx-auto">
                            <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2 lg:gap-16">
                                <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
                                    <img
                                        src="/esg-performance.png"
                                        alt="ESG Performance Management Dashboard"
                                        className="object-contain w-full h-auto max-w-md"
                                    />
                                </div>
                                <div className="order-1 lg:order-2">
                                    <h2 className="mb-6 text-black" style={{ fontFamily: 'Montserrat', fontWeight: 700, fontSize: '40px', lineHeight: '60px', textTransform: 'capitalize' }}>
                                        Struggling to Measure and Manage ESG Performance at Scale?
                                    </h2>
                                    <p className="mb-8 text-gray-600" style={{ fontFamily: 'Montserrat', fontWeight: 500, fontSize: '20px', lineHeight: '36px' }}>
                                        Vajra simplifies ESG management by consolidating environmental, social, and governance data, providing teams with a clear, real-time view across operations and business units, making it easy for companies to track data, generate reports, and meet compliance, cost-effectively.
                                    </p>
                                    <div className="flex flex-col gap-4 sm:flex-row">
                                        <button
                                            onClick={handleBookDemo}
                                            className="flex items-center justify-center gap-2 px-6 py-3 text-base font-medium text-white transition-all duration-300 rounded-lg md:text-lg hover:bg-opacity-90 montserrat"
                                            style={{ backgroundColor: '#1C486B' }}
                                        >
                                            Book a Demo
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}

export default Cbam;